/**
 * AddContactManualModal.tsx - VERSIÓN CORREGIDA
 * 
 * Modal para agregar contactos manualmente con formulario completo.
 * FIXES v1.1:
 * - Tipo 'status' corregido (bounced/unsubscribed en lugar de blocked)
 * - Estilos Bootstrap nativos (form-control, form-label, btn)
 * - Modal con fondo oscuro y blur
 * - Colores según diseño del front (#2ca799 teal)
 * 
 * @version 1.1.0
 * @date 2024-11-07
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faUser,
  faPhone,
  faEnvelope,
  faBuilding,
  faBriefcase,
  faUsers,
  faTags,
  faToggleOn,
  faStickyNote,
  faCheck,
  faExclamationTriangle,
  faInfoCircle,
  faSave
} from '@fortawesome/free-solid-svg-icons';

// Hooks
import { useContactForm } from '../../../hooks/useContactForm';
import type { ContactDB, ContactGroup, ContactTag } from '../../../data/mockContactsDatabase';

// ==================== 📋 INTERFACES ====================

export interface AddContactManualModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: (contact: ContactDB) => void;
  groups: ContactGroup[];
  tags: ContactTag[];
  checkDuplicatePhone?: (phone: string) => ContactDB | undefined;
  addContact?: (contact: Omit<ContactDB, 'id' | 'created_at' | 'updated_at'>) => Promise<ContactDB>;
}

// ==================== 🎨 COMPONENTE ====================

const AddContactManualModal: React.FC<AddContactManualModalProps> = ({
  show,
  onHide,
  onSuccess,
  groups,
  tags,
  checkDuplicatePhone,
  addContact
}) => {

  // ───────────────────────────────────────────────────────────────────────
  // HOOK
  // ───────────────────────────────────────────────────────────────────────

  const {
    formData,
    errors,
    isSubmitting,
    isDuplicate,
    duplicateContact,
    handleChange,
    handleSubmit,
    resetForm,
    isValid,
    touchedFields,
    setFieldTouched
  } = useContactForm({
    onSuccess: (contact) => {
      if (onSuccess) onSuccess(contact);
      onHide();
    },
    onError: (error) => {
      console.error('Error:', error);
    },
    checkDuplicatePhone,
    addContact
  });

  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────────

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit();
  };

  // Toggle grupo
  const toggleGroup = (groupName: string) => {
    const newGroups = formData.groups.includes(groupName)
      ? formData.groups.filter(g => g !== groupName)
      : [...formData.groups, groupName];
    handleChange('groups', newGroups);
  };

  // Toggle tag
  const toggleTag = (tagName: string) => {
    const newTags = formData.tags.includes(tagName)
      ? formData.tags.filter(t => t !== tagName)
      : [...formData.tags, tagName];
    handleChange('tags', newTags);
  };

  // ───────────────────────────────────────────────────────────────────────
  // RENDER HELPERS
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Icono de validación para un campo
   */
  const renderValidationIcon = (field: keyof typeof formData, required: boolean = false) => {
    const isTouched = touchedFields.has(field);
    const hasError = errors[field as keyof typeof errors];
    const hasValue = formData[field] && String(formData[field]).trim().length > 0;

    if (!isTouched && !hasValue && !required) return null;

    if (hasError) {
      return (
        <span style={{ color: '#dc3545', marginLeft: '8px' }}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </span>
      );
    }

    if (hasValue && !hasError && field !== 'groups' && field !== 'tags') {
      return (
        <span style={{ color: '#198754', marginLeft: '8px' }}>
          <FontAwesomeIcon icon={faCheck} />
        </span>
      );
    }

    return null;
  };

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: MODAL CONTENT
  // ───────────────────────────────────────────────────────────────────────

  const modalContent = (
    <div 
      onClick={handleClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.85)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        padding: '20px',
        overflowY: 'auto'
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--bs-body-bg)',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          width: '100%',
          maxWidth: '700px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--bs-border-color)'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--bs-border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'var(--bs-body-bg)'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: 600,
            color: 'var(--bs-body-color)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <FontAwesomeIcon icon={faUser} style={{ color: '#2ca799' }} />
            Agregar Nuevo Contacto
          </h3>
          
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="btn-close"
            aria-label="Close"
            style={{
              opacity: isSubmitting ? 0.5 : 1
            }}
          />
        </div>

        {/* Body */}
        <form onSubmit={handleFormSubmit} style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ padding: '24px' }}>
            
            {/* Información Básica */}
            <div style={{ marginBottom: '32px' }}>
              <h6 className="text-uppercase text-muted mb-3" style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.5px'
              }}>
                Información Básica
              </h6>

              {/* Nombre */}
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  <FontAwesomeIcon icon={faUser} style={{ marginRight: '8px', color: 'var(--bs-secondary)' }} />
                  Nombre Completo *
                  {renderValidationIcon('name', true)}
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.name && touchedFields.has('name') ? 'is-invalid' : ''}`}
                  placeholder="Ej: Juan Pérez González"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  onBlur={() => setFieldTouched('name')}
                  disabled={isSubmitting}
                />
                {errors.name && touchedFields.has('name') && (
                  <div className="invalid-feedback" style={{ display: 'block' }}>
                    {errors.name}
                  </div>
                )}
              </div>

              {/* Teléfono y Email */}
              <div className="row g-3 mb-3">
                {/* Teléfono */}
                <div className="col-md-6">
                  <label className="form-label" style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    <FontAwesomeIcon icon={faPhone} style={{ marginRight: '8px', color: 'var(--bs-secondary)' }} />
                    Teléfono *
                    {renderValidationIcon('phone', true)}
                  </label>
                  <input
                    type="tel"
                    className={`form-control ${(errors.phone && touchedFields.has('phone')) || isDuplicate ? 'is-invalid' : ''}`}
                    placeholder="+56912345678"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    onBlur={() => setFieldTouched('phone')}
                    disabled={isSubmitting}
                  />
                  {errors.phone && touchedFields.has('phone') && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      {errors.phone}
                    </div>
                  )}
                </div>

                {/* Email */}
                <div className="col-md-6">
                  <label className="form-label" style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '8px', color: 'var(--bs-secondary)' }} />
                    Email
                    {renderValidationIcon('email')}
                  </label>
                  <input
                    type="email"
                    className={`form-control ${errors.email && touchedFields.has('email') ? 'is-invalid' : ''}`}
                    placeholder="correo@ejemplo.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    onBlur={() => setFieldTouched('email')}
                    disabled={isSubmitting}
                  />
                  {errors.email && touchedFields.has('email') && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      {errors.email}
                    </div>
                  )}
                </div>
              </div>

              {/* Duplicado Warning */}
              {isDuplicate && duplicateContact && (
                <div className="alert alert-danger d-flex align-items-center mb-3" role="alert">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  <div>
                    <strong>Este teléfono ya existe en la base de datos</strong>
                    <br />
                    <small>Contacto existente: {duplicateContact.name}</small>
                  </div>
                </div>
              )}
            </div>

            {/* Información Profesional */}
            <div style={{ marginBottom: '32px' }}>
              <h6 className="text-uppercase text-muted mb-3" style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.5px'
              }}>
                Información Profesional
              </h6>

              <div className="row g-3">
                {/* Empresa */}
                <div className="col-md-6">
                  <label className="form-label" style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    <FontAwesomeIcon icon={faBuilding} style={{ marginRight: '8px', color: 'var(--bs-secondary)' }} />
                    Empresa
                    {renderValidationIcon('company')}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: Acme Corp"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    onBlur={() => setFieldTouched('company')}
                    disabled={isSubmitting}
                  />
                </div>

                {/* Cargo */}
                <div className="col-md-6">
                  <label className="form-label" style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    <FontAwesomeIcon icon={faBriefcase} style={{ marginRight: '8px', color: 'var(--bs-secondary)' }} />
                    Cargo
                    {renderValidationIcon('position')}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: Gerente de Ventas"
                    value={formData.position}
                    onChange={(e) => handleChange('position', e.target.value)}
                    onBlur={() => setFieldTouched('position')}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            {/* Segmentación */}
            <div style={{ marginBottom: '32px' }}>
              <h6 className="text-uppercase text-muted mb-3" style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.5px'
              }}>
                Segmentación
              </h6>

              {/* Grupos */}
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  <FontAwesomeIcon icon={faUsers} style={{ marginRight: '8px', color: 'var(--bs-secondary)' }} />
                  Grupos
                </label>
                <div className="d-flex flex-wrap gap-2">
                  {groups.map(group => (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => toggleGroup(group.name)}
                      disabled={isSubmitting}
                      className={`btn btn-sm ${formData.groups.includes(group.name) ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{
                        opacity: isSubmitting ? 0.5 : 1
                      }}
                    >
                      {formData.groups.includes(group.name) && (
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />
                      )}
                      {group.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  <FontAwesomeIcon icon={faTags} style={{ marginRight: '8px', color: 'var(--bs-secondary)' }} />
                  Tags
                </label>
                <div className="d-flex flex-wrap gap-2">
                  {tags.map(tag => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTag(tag.name)}
                      disabled={isSubmitting}
                      className={`btn btn-sm ${formData.tags.includes(tag.name) ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{
                        opacity: isSubmitting ? 0.5 : 1,
                        fontSize: '0.75rem'
                      }}
                    >
                      {formData.tags.includes(tag.name) && (
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px', fontSize: '0.625rem' }} />
                      )}
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Estado */}
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  <FontAwesomeIcon icon={faToggleOn} style={{ marginRight: '8px', color: 'var(--bs-secondary)' }} />
                  Estado
                </label>
                <select
                  className="form-select"
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value as any)}
                  disabled={isSubmitting}
                  style={{ maxWidth: '200px' }}
                >
                  <option value="active">🟢 Activo</option>
                  <option value="inactive">🟡 Inactivo</option>
                  <option value="bounced">🔴 Rebotado</option>
                  <option value="unsubscribed">⚫ Desuscrito</option>
                </select>
              </div>
            </div>

            {/* Notas */}
            <div className="mb-3">
              <label className="form-label" style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                <FontAwesomeIcon icon={faStickyNote} style={{ marginRight: '8px', color: 'var(--bs-secondary)' }} />
                Notas
              </label>
              <textarea
                className="form-control"
                placeholder="Observaciones adicionales sobre el contacto..."
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                disabled={isSubmitting}
                rows={4}
                style={{ resize: 'vertical' }}
              />
            </div>

            {/* Info Helper */}
            <div className="alert alert-info d-flex gap-2" role="alert">
              <FontAwesomeIcon icon={faInfoCircle} style={{ marginTop: '2px' }} />
              <small>
                Los campos marcados con <strong>*</strong> son obligatorios. 
                El teléfono debe tener formato chileno (+56XXXXXXXXX).
              </small>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--bs-border-color)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          backgroundColor: 'var(--bs-light)'
        }}>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="btn btn-secondary"
          >
            Cancelar
          </button>
          
          <button
            type="submit"
            onClick={handleFormSubmit}
            disabled={isSubmitting || !isValid || isDuplicate}
            className="btn btn-primary"
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Guardando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Guardar Contacto
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────────

  if (!show) return null;

  return createPortal(modalContent, document.body);
};

export default AddContactManualModal;